import { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { Skeleton } from 'primereact/skeleton';
import AddCategoryModal from "./AddCategoryModal";
import CategoryCard from "./CategoryCard";
import AuthContext from "../../../../auth/AuthContext";
import CategoryService from "../../../../services/CategoryService";
import AlertService from "../../../../services/AlertService";
import ConfirmDialogBox from "../../../confirmDialog/ConfirmDialogBox";

const Catagories = () => {
  const [fullCategories, setFullCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [permissions, setPermissions] = useState({});

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  useEffect(() => {
    const catalogueAccess = authContext.getPermissions("category") || [];
    if (catalogueAccess.filter(f => f.access.includes(1)).length === 0 || authContext.onGoingValidations) {
      navigate("/");
    }
    setPermissions({
      showAdd: catalogueAccess.some(f => f.access.includes(2)),
      showEdit: catalogueAccess.some(f => f.access.includes(3)),
      showDelete: catalogueAccess.some(f => f.access.includes(4)),
    });
  }, [navigate, authContext]);

const loadCategories = useCallback(async (signal) => {
    setLoading(true);
    try {
      const data = await CategoryService.GetAllCategories(
        authContext,
        signal 
      );

      if (signal?.aborted) return;

      if (data.isSuccess) {
        setFullCategories(data.data.all);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadCategories(controller.signal); 
    return () => controller.abort(); 
  }, [loadCategories]);

  const handleAddClick = () => {
    setIsEditMode(false);
    setSelectedCategory(null);
    setShowModal(true);
  };

  const handleClick = (title, id) => {
    const slug = title.toLowerCase().replace(/\s+/g, "-");
    navigate(`/catalogue-dashboard/catalogue/${slug}`, { state: { title, value: id } });
  };

  const confirmDelete = (category) => {
    setCategoryToDelete(category);
    setShowDeleteDialog(true);
  };

  const performDelete = async () => {
    setShowDeleteDialog(false);
    if (categoryToDelete) {
      const result = await CategoryService.Remove(categoryToDelete.id, authContext);
      if (result.isSuccess) {
        loadCategories();
        AlertService.success("Category deleted successfully!");
      } else {
        AlertService.error(result.errorMessage);
      }
    }
  };


  return (
    <div className="container-fluid custom-container mt-3">
      {permissions.showAdd && (
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary py-2" onClick={handleAddClick}>
            <FaPlus className="pb-1 me-1" />
            Add New Category
          </button>
        </div>
      )}

      <div className="row g-4 mb-4">
        {loading ? (
          Array.from({ length: 8 }).map((_, idx) => (
            <div key={idx} className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3">
              <div className="card border-0 shadow-sm p-3 h-100 d-flex flex-row align-items-center gap-3">
                <Skeleton shape="circle" size="3rem" className="flex-shrink-0" />
                <div className="flex-grow-1">
                  <Skeleton width="80%" height="1.2rem" />
                </div>
              </div>
            </div>
          ))
        ) : fullCategories.length > 0 ? (
          fullCategories.map(cat => (
            <div
              key={cat.id}
              className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3"
              onClick={() => handleClick(cat.categoryName, cat.id)}
              style={{ cursor: "pointer" }}
            >
              <CategoryCard
                title={cat.categoryName}
                onEdit={permissions.showEdit ? () => {
                  setSelectedCategory(cat);
                  setIsEditMode(true);
                  setShowModal(true);
                } : null}
                onDelete={permissions.showDelete ? () => confirmDelete(cat) : null}
              />
            </div>
          ))
        ) : (
          <div className="col-12 text-center">No category available...</div>
        )}
      </div>

      <AddCategoryModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        isEditMode={isEditMode}
        initialCategory={selectedCategory}
        reloadList={loadCategories}
      />

      <ConfirmDialogBox
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onConfirm={performDelete}
        title={
          <>
            Are you sure you want to delete the category{" "}
            <strong>{categoryToDelete?.categoryName}</strong>?
          </>
        }
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default Catagories;
